import { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function getOrCreateSessionId() {
  const key = 'pmi_session_id';
  let sessionId = localStorage.getItem(key);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    localStorage.setItem(key, sessionId);
  }
  return sessionId;
}

export default function AnalyticsConsentBanner() {
  const [checked, setChecked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const existing = localStorage.getItem('pmi_analytics_consent');
    if (existing === 'true') {
      setChecked(true);
      setSaved(true);
      setDismissed(true); // Don't show if already consented
    }
    if (existing === 'false') {
      setDismissed(true); // Don't show if explicitly opted out
    }
  }, []);

  async function saveConsent(value) {
    const sessionId = getOrCreateSessionId();
    try {
      const res = await fetch(`${API_URL}/api/analytics/consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          consent_analytics: value,
          consent_version: 'v1',
        }),
      });
      if (res.ok) {
        localStorage.setItem('pmi_analytics_consent', String(value));
        setChecked(value);
        setSaved(true);
        setTimeout(() => setDismissed(true), 2000);
      }
    } catch {
      // Silent fail
    }
  }

  if (dismissed) return null;

  return (
    <div className="fixed bottom-2 sm:bottom-14 left-2 right-2 sm:left-auto sm:right-4 sm:max-w-sm z-40" data-testid="analytics-consent-banner">
      <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3 sm:p-4 shadow-2xl shadow-black/50">
        <p className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2">Help improve Trade PMI</p>
        <p className="hidden sm:block text-xs text-zinc-400 mb-3 leading-relaxed">
          Share pseudonymous usage data for product improvement and aggregated platform reporting only.
        </p>
        <label className="flex items-start gap-2.5 text-xs text-zinc-400 cursor-pointer mb-3">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="mt-0.5 w-3.5 h-3.5 rounded border-zinc-600 bg-zinc-800 accent-brand flex-shrink-0"
            data-testid="analytics-consent-checkbox"
          />
          <span>
            I agree to share usage events such as market selected, analysis run, and feature usage.
            No personal details, trading activity or identity are ever collected.
          </span>
        </label>
        <div className="flex gap-2">
          <button
            onClick={() => saveConsent(checked)}
            className="bg-brand hover:bg-brand-light text-black text-xs font-bold px-3 py-1.5 rounded-md transition-colors"
            data-testid="analytics-save-btn"
          >
            Save preference
          </button>
          <button
            onClick={() => { saveConsent(false); setDismissed(true); }}
            className="border border-zinc-700 text-zinc-400 text-xs px-3 py-1.5 rounded-md hover:border-zinc-500 transition-all"
            data-testid="analytics-optout-btn"
          >
            Opt out
          </button>
        </div>
        {saved && (
          <p className="mt-2 text-[10px] text-zinc-600">
            Preference saved. Analytics remains improvement-only and aggregated.
          </p>
        )}
      </div>
    </div>
  );
}
